import { FetchResult, useMutation } from '@apollo/client';
import { useState } from 'react';

import {
  AmazonOfferError,
  AmazonStoreError,
  CartError,
  CreateOrder_SubmitCartMutation,
  ShopifyOfferError,
  ShopifyStoreError,
  Store,
} from 'app/graphql/generated/cart/graphql';
import {
  CREATE_CART,
  SET_CART_ATTRIBUTES,
  SUBMIT_CART,
  UPDATE_BUYER_IDENTITY,
} from 'app/graphql/cart-api/index';
import { BuyerIdentityInput, Cart, CartItemsInput } from '../graphql';
import { GraphQLFormattedError } from 'graphql';

// The billing export reconciles orders back to a developer's own system via a
// cart attribute keyed `External-ID` (see checkout-core export-billing-job). The
// manual-order flow attaches it under the same key so those orders aren't left
// without an external id on the invoice.
const EXTERNAL_ID_ATTRIBUTE_KEY = 'External-ID';

export type UseCartErrors =
  | Array<CartError | AmazonStoreError | ShopifyStoreError | ShopifyOfferError | AmazonOfferError>
  | Readonly<Array<GraphQLFormattedError>>;

export interface CartState {
  cart: Cart | null;
  errors: UseCartErrors;

  isUpdating: boolean;
  isSubmitting: boolean;
}

export interface CartActions {
  createCart: (items: CartItemsInput) => Promise<void>;
  updateCartBuyerIdentity: (buyerIdentity: BuyerIdentityInput) => Promise<void>;

  /**
   * Attach the developer's own order reference to the cart as the `External-ID`
   * attribute, so the order is reconcilable on the billing export. Returns true
   * on success; false if there's no cart or the mutation reported an error, so
   * the caller can abort the submit rather than place an order that would be
   * missing its external id.
   */
  setExternalId: (externalId: string) => Promise<boolean>;

  submitCart: () => Promise<FetchResult<CreateOrder_SubmitCartMutation>>;
}

type UseCartResult = [CartState, CartActions];

export function useCart(): UseCartResult {
  const [cart, setCart] = useState<Cart | null>(null);
  const [errors, setErrors] = useState<UseCartErrors>([]);

  const [createCart, { loading: isCreating }] = useMutation(CREATE_CART);
  const [updateBuyerIdentity, { loading: isUpdatingBuyerIdentity }] =
    useMutation(UPDATE_BUYER_IDENTITY);
  const [setCartAttributes, { loading: isSettingCartAttributes }] =
    useMutation(SET_CART_ATTRIBUTES);
  const [submitCart, { loading: isSubmitting }] = useMutation(SUBMIT_CART);

  // `isSettingCartAttributes` keeps the submit button disabled during the
  // pre-submit External-ID write, closing a double-submit window.
  const isUpdating = isCreating || isUpdatingBuyerIdentity || isSettingCartAttributes;

  const actions: CartActions = {
    async createCart(items) {
      await createCart({
        variables: {
          input: {
            items,
            cartSettings: {
              amazonSettings: {
                hidePriceOnPackage: true,
              },
            },
          },
        },
      }).then((res) => {
        if (res.data) {
          setCart(res.data.createCart.cart);
          setErrors([
            ...res.data.createCart.errors,
            ...res.data.createCart.cart.stores.flatMap((store: Store) => store.errors),
            ...res.data.createCart.cart.stores.flatMap((store: Store) => store.offer?.errors ?? []),
          ]);
        } else if (res.errors) {
          setCart(null);
          setErrors(res.errors);
        }
      });
    },
    async updateCartBuyerIdentity(buyerIdentity) {
      if (cart) {
        await updateBuyerIdentity({
          variables: {
            input: {
              id: cart.id,
              buyerIdentity,
            },
          },
        }).then((res) => {
          if (res.data) {
            setCart(res.data.updateCartBuyerIdentity.cart);
            setErrors([
              ...res.data.updateCartBuyerIdentity.errors,
              ...res.data.updateCartBuyerIdentity.cart.stores.flatMap(
                (store: Store) => store.errors,
              ),
              ...res.data.updateCartBuyerIdentity.cart.stores.flatMap(
                (store: Store) => store.offer?.errors ?? [],
              ),
            ]);
          } else if (res.errors) {
            setCart(null);
            setErrors(res.errors);
          }
        });
      }
    },
    async setExternalId(externalId) {
      if (!cart) {
        return false;
      }

      // An empty identifier clears the attribute. `setCartAttributes` replaces
      // the whole attribute set, and this flow only ever manages `External-ID`,
      // so an empty array is a safe clear that leaves no stale value behind.
      const attributes = externalId ? [{ key: EXTERNAL_ID_ATTRIBUTE_KEY, value: externalId }] : [];

      try {
        const res = await setCartAttributes({
          variables: { id: cart.id, attributes },
        });

        // A failure here isn't a buyer-identity/offer error, so don't route it
        // into the shared cart `errors` state (which drives field-level form
        // errors + the availability calc). The caller surfaces the failure and
        // aborts the submit instead.
        const userErrors = res.data?.setCartAttributes?.userErrors ?? [];
        return !res.errors && userErrors.length === 0;
      } catch {
        // The default Apollo errorPolicy rejects on transport/GraphQL errors;
        // honor the Promise<boolean> contract so the caller shows its fallback
        // instead of the throw escaping the submit handler.
        return false;
      }
    },
    async submitCart() {
      if (cart) {
        const selectedShippingOptions = cart.stores.map((store) => ({
          store: store.store,
          shippingId: store.offer!.shippingMethods[0].id,
        }));

        return submitCart({
          variables: {
            input: { id: cart.id, selectedShippingOptions, token: 'free-payment' },
          },
        });
      }

      throw new Error('No cart! Panic.');
    },
  };

  return [{ cart, errors, isSubmitting, isUpdating }, actions];
}
